import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Box, Flex, HStack, IconButton, Text, VStack } from '@chakra-ui/react';
import { AdvisorProfileCard } from '@src/components/sco/Advisors/cards/AdvisorProfileCard';
import { useAdvisorBoard, filterEngagements } from '@src/components/sco/Advisors/hooks/useAdvisorBoard';
import { useAdvisorsCollectionLoadMore } from '@src/components/sco/Advisors/hooks/useAdvisorsCollectionLoadMore';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { useDirectChatUnreadCount } from '@src/components/sco/Advisors/hooks/useDirectChatUnreadCount';
import { useIsMobile } from '@src/components/sco/Advisors/hooks/useIsMobile';
import { ConversationList } from '@src/components/sco/Advisors/layout/ConversationList';
import { EngagementAsidePanel } from '@src/components/sco/Advisors/layout/EngagementAsidePanel';
import { InboxChatPane } from '@src/components/sco/Advisors/layout/InboxChatPane';
import { InboxStartCtas } from '@src/components/sco/Advisors/layout/InboxStartCtas';
import { LockedTeamAdvisorMiniProfile } from '@src/components/sco/Advisors/layout/LockedTeamAdvisorMiniProfile';
import { LOCKED_TEAM_ADVISORS, useAdvisorsAccess } from '@src/components/sco/Advisors/model/access';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { buildConversationRows, ConversationRow, conversationRowKey, conversationSubjectUserId, isRealInboxThread, pickDefaultInboxRow } from '@src/components/sco/Advisors/utils/conversationRows';
import { engagementDisplayName } from '@src/components/sco/Advisors/utils/mapEngagements';
import { founderDisplayName, founderUserIdOf, isActiveFounderEngagement } from '@src/components/sco/Advisors/utils/mapFounderEngagements';
import { NestedAppLayout, attachedAsidePanelContainerProps, attachedNavPanelContainerProps, attachedPanelContainerProps } from '@src/components/sco/AppLayout';
import { useParentAppLayout } from '@src/components/sco/AppLayout/AppLayoutContext';
import { AppPanelToggleButton } from '@src/components/sco/AppLayout/Panel/AppPanelToggleButton';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_ENGAGEMENT_STATUS_ACTIVE } from '@src/constants/advisor-engagement-statuses';
import { ADVISOR_FOUNDER_ENGAGEMENTS_WITH_CHAT } from '@src/gql/queries/AdvisorFounderEngagements';
import { INBOX_DIRECT_ADVISOR } from '@src/gql/queries/InboxDirectAdvisor';
import { ScoAdvisorEngagement, ScoUser } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useRouter } from 'next/router';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { LuArrowLeft } from 'react-icons/lu';
const INBOX_NAV_ID = 'advisors-inbox-nav';
const INBOX_ASIDE_ID = 'advisors-inbox-aside';
const InboxNavColumn = t0 => {
  const $ = _c(12);
  const {
    isInitialLoading,
    rows,
    selectedKey,
    onSelect,
    listFooter
  } = t0;
  const layout = useParentAppLayout();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <InboxStartCtas size="xs" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== layout || $[2] !== onSelect) {
    t2 = row => {
      onSelect(row);
      layout.closeDrawer?.(INBOX_NAV_ID);
    };
    $[1] = layout;
    $[2] = onSelect;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== listFooter || $[5] !== rows || $[6] !== selectedKey || $[7] !== t2) {
    t3 = <ConversationList emptyFooter={t1} emptyMessage="Message an advisor from Find advisors, invite them to a board seat, or Ask Wil." listFooter={listFooter} rows={rows} selectedKey={selectedKey} onSelect={t2} />;
    $[4] = listFooter;
    $[5] = rows;
    $[6] = selectedKey;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== isInitialLoading || $[10] !== t3) {
    t4 = <VStack align="stretch" gap={0} height="full" minHeight={0} overflowY="auto" width="full"><AdvisorsQueryState error={undefined} loading={isInitialLoading}>{t3}</AdvisorsQueryState></VStack>;
    $[9] = isInitialLoading;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};

/**
 * Thread header. On a phone the list is its own screen, so the header gets a back link to it instead
 * of a second menu toggle (#17, #19).
 */
const InboxThreadChrome = t0 => {
  const $ = _c(13);
  const {
    title,
    backHref,
    children
  } = t0;
  let t1;
  if ($[0] !== backHref) {
    t1 = backHref ? <IconButton asChild={true} aria-label="All conversations" size="sm" variant="outlineGhost"><NextLink href={backHref}><LuArrowLeft /></NextLink></IconButton> : null;
    $[0] = backHref;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <Text flex={1} fontWeight="semibold" lineClamp={1} minWidth={0}>{title}</Text>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <AppPanelToggleButton panelId={INBOX_ASIDE_ID} panelPosition="right" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t2) {
    t4 = <HStack borderBottomWidth="1px" borderColor="colorPalette.border/40" flexShrink={0} gap={2} justify="space-between" paddingX={4} paddingY={2}>{t1}{t2}{t3}</HStack>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== children) {
    t5 = <Box display="flex" flex={1} flexDirection="column" minHeight={0}>{children}</Box>;
    $[8] = children;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <Flex direction="column" flex={1} height="full" minHeight={0} minWidth={0} width="full">{t4}{t5}</Flex>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};

/** Full roster — the inbox scrolls, so it is not held to the old sidebar window. */
const INBOX_PAGE_SIZE = 100;
type FounderEngagementsResult = {
  advisorEngagements?: {
    items?: Array<ScoAdvisorEngagement | null> | null;
    pagination?: {
      hasNextPage?: boolean | null;
    } | null;
  } | null;
};
const readParam = (value: string | string[] | undefined): string | undefined => {
  if (typeof value === 'string' && value) {
    return value;
  }
  if (Array.isArray(value) && typeof value[0] === 'string' && value[0]) {
    return value[0];
  }
  return undefined;
};

/**
 * `/advisors/inbox` — conversations list on the left, chat on the right,
 * collapsible profile on the far right.
 *
 * Replaces the Conversations rail that used to live in the Advisors left nav.
 * Selection stays in the URL (`?advisorUserId=` / `?founderUserId=` /
 * `?advisorKey=` for locked upsell rows) so threads remain linkable — the same
 * params the superseded `/ask-advisors` and `/my-founders` chat routes used.
 */
export const InboxSection = () => {
  const $ = _c(122);
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const {
    currentUser
  } = useCurrentUser();
  const {
    canAccessAdvisorTeam
  } = useAdvisorsAccess();
  const isMobile = useIsMobile();
  const didDefaultSelectRef = useRef(false);
  let t0;
  if ($[0] !== paths.inbox) {
    t0 = advisorsBreadcrumbsWithTrail({
      title: "Messages",
      href: paths.inbox
    });
    $[0] = paths.inbox;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      breadcrumbs: t0
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useAdvisorsToolbar(t1);
  let t2;
  if ($[4] !== router.query.advisorUserId) {
    t2 = readParam(router.query.advisorUserId);
    $[4] = router.query.advisorUserId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const selectedAdvisorUserId = t2;
  let t3;
  if ($[6] !== router.query.founderUserId) {
    t3 = readParam(router.query.founderUserId);
    $[6] = router.query.founderUserId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const selectedFounderUserId = t3;
  let t4;
  if ($[8] !== router.query.advisorKey) {
    t4 = readParam(router.query.advisorKey);
    $[8] = router.query.advisorKey;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const selectedAdvisorKey = t4;
  const boardQuery = useAdvisorBoard();
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      variables: {
        statusKeys: [ADVISOR_ENGAGEMENT_STATUS_ACTIVE],
        page: 1,
        pageSize: INBOX_PAGE_SIZE
      },
      fetchPolicy: "cache-and-network"
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const foundersQuery = useQuery(ADVISOR_FOUNDER_ENGAGEMENTS_WITH_CHAT, t5);
  foundersQuery.data?.advisorEngagements?.items;
  let t6;
  if ($[11] !== foundersQuery.data?.advisorEngagements?.items) {
    t6 = foundersQuery.data?.advisorEngagements?.items || [];
    $[11] = foundersQuery.data?.advisorEngagements?.items;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = t6.filter(isActiveFounderEngagement);
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const firstPageFounders = t7 as ScoAdvisorEngagement[];
  const t8 = foundersQuery.data?.advisorEngagements?.pagination?.hasNextPage;
  let t9;
  if ($[15] !== firstPageFounders || $[16] !== foundersQuery.fetchMore || $[17] !== t8) {
    t9 = {
      fetchMore: foundersQuery.fetchMore,
      field: "advisorEngagements",
      firstPageItems: firstPageFounders,
      hasNextPage: t8
    };
    $[15] = firstPageFounders;
    $[16] = foundersQuery.fetchMore;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const {
    items: founderEngagements,
    loadMore: loadMoreFounders,
    loadingMore: loadingMoreFounders,
    hasNextPage: foundersHasNext
  } = useAdvisorsCollectionLoadMore(t9);
  const {
    unreadCount
  } = useDirectChatUnreadCount();
  const prevUnreadRef = useRef(null);
  boardQuery.data?.advisorBoard;
  const t10 = boardQuery.data?.advisorBoard;
  let t11;
  if ($[19] !== t10) {
    t11 = filterEngagements(t10);
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const boardEngagements = t11;
  let t12;
  bb0: {
    if (!selectedAdvisorUserId) {
      t12 = false;
      break bb0;
    }
    let t13;
    if ($[21] !== boardEngagements || $[22] !== founderEngagements || $[23] !== selectedAdvisorUserId) {
      t13 = boardEngagements.some(engagement_0 => (engagement_0.advisorUserId || engagement_0.advisorUser?.userId) === selectedAdvisorUserId) || founderEngagements.some(engagement => founderUserIdOf(engagement) === selectedAdvisorUserId);
      $[21] = boardEngagements;
      $[22] = founderEngagements;
      $[23] = selectedAdvisorUserId;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    t12 = t13;
  }
  const hasEngagementWithAdvisor = t12;
  const skipDirectAdvisorQuery = !selectedAdvisorUserId || hasEngagementWithAdvisor || selectedAdvisorUserId === currentUser?.userId;
  let t13;
  if ($[25] !== selectedAdvisorUserId) {
    t13 = {
      userId: selectedAdvisorUserId
    };
    $[25] = selectedAdvisorUserId;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== skipDirectAdvisorQuery || $[28] !== t13) {
    t14 = {
      variables: t13,
      skip: skipDirectAdvisorQuery,
      fetchPolicy: "cache-first"
    };
    $[27] = skipDirectAdvisorQuery;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const directAdvisorQuery = useQuery(INBOX_DIRECT_ADVISOR, t14);
  currentUser?.userId;
  directAdvisorQuery.data?.userById;
  let t15;
  if ($[30] !== boardEngagements || $[31] !== canAccessAdvisorTeam || $[32] !== currentUser?.userId || $[33] !== directAdvisorQuery.data?.userById?.displayName || $[34] !== directAdvisorQuery.data?.userById?.fullName || $[35] !== directAdvisorQuery.data?.userById?.pictureUrl || $[36] !== founderEngagements || $[37] !== hasEngagementWithAdvisor || $[38] !== selectedAdvisorUserId) {
    t15 = buildConversationRows({
      boardEngagements,
      founderEngagements,
      lockedAdvisors: canAccessAdvisorTeam ? [] : [...LOCKED_TEAM_ADVISORS],
      directAdvisors: selectedAdvisorUserId && !hasEngagementWithAdvisor && selectedAdvisorUserId !== currentUser?.userId ? [{
        userId: selectedAdvisorUserId,
        name: directAdvisorQuery.data?.userById?.fullName || directAdvisorQuery.data?.userById?.displayName || "Advisor",
        pictureUrl: directAdvisorQuery.data?.userById?.pictureUrl
      }] : []
    });
    $[30] = boardEngagements;
    $[31] = canAccessAdvisorTeam;
    $[32] = currentUser?.userId;
    $[33] = directAdvisorQuery.data?.userById?.displayName;
    $[34] = directAdvisorQuery.data?.userById?.fullName;
    $[35] = directAdvisorQuery.data?.userById?.pictureUrl;
    $[36] = founderEngagements;
    $[37] = hasEngagementWithAdvisor;
    $[38] = selectedAdvisorUserId;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const rows = t15;
  let t16;
  if ($[40] !== canAccessAdvisorTeam || $[41] !== rows) {
    let t17;
    if ($[43] !== canAccessAdvisorTeam) {
      t17 = row => isRealInboxThread(row, canAccessAdvisorTeam);
      $[43] = canAccessAdvisorTeam;
      $[44] = t17;
    } else {
      t17 = $[44];
    }
    t16 = rows.some(t17);
    $[40] = canAccessAdvisorTeam;
    $[41] = rows;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  const hasRealThread = t16;
  let t17;
  bb1: {
    if (selectedFounderUserId) {
      let t18;
      if ($[45] !== rows || $[46] !== selectedFounderUserId) {
        t18 = rows.find(row_0 => row_0.kind === "founder" && founderUserIdOf(row_0.engagement) === selectedFounderUserId) ?? null;
        $[45] = rows;
        $[46] = selectedFounderUserId;
        $[47] = t18;
      } else {
        t18 = $[47];
      }
      t17 = t18;
      break bb1;
    }
    if (selectedAdvisorKey) {
      let t18;
      if ($[48] !== rows || $[49] !== selectedAdvisorKey) {
        t18 = rows.find(row_1 => row_1.kind === "locked" && row_1.advisor.navKey === selectedAdvisorKey) ?? null;
        $[48] = rows;
        $[49] = selectedAdvisorKey;
        $[50] = t18;
      } else {
        t18 = $[50];
      }
      t17 = t18;
      break bb1;
    }
    if (selectedAdvisorUserId) {
      let t18;
      if ($[51] !== rows || $[52] !== selectedAdvisorUserId) {
        t18 = rows.find(row_2 => (row_2.kind === "board" || row_2.kind === "direct") && conversationSubjectUserId(row_2) === selectedAdvisorUserId) ?? null;
        $[51] = rows;
        $[52] = selectedAdvisorUserId;
        $[53] = t18;
      } else {
        t18 = $[53];
      }
      t17 = t18;
      break bb1;
    }
    t17 = null;
  }
  const selectedRow = t17;
  let t18;
  if ($[54] !== paths) {
    t18 = row_3 => {
      if (row_3.kind === "board" && row_3.engagement.isAI) {
        return paths.ask;
      }
      if (row_3.kind === "founder") {
        return paths.withQuery(paths.inbox, {
          founderUserId: founderUserIdOf(row_3.engagement)
        });
      }
      if (row_3.kind === "locked") {
        return paths.withQuery(paths.inbox, {
          advisorKey: row_3.advisor.navKey
        });
      }
      if (row_3.kind === "direct") {
        return paths.withQuery(paths.inbox, {
          advisorUserId: row_3.userId
        });
      }
      return paths.withQuery(paths.inbox, {
        advisorUserId: row_3.engagement.advisorUserId || row_3.engagement.advisorUser?.userId
      });
    };
    $[54] = paths;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  const hrefForRow = t18;
  let t19;
  if ($[56] !== hrefForRow || $[57] !== router) {
    t19 = row_4 => {
      const href = hrefForRow(row_4);
      if (row_4.kind === "board" && row_4.engagement.isAI) {
        router.push(href);
        return;
      }
      router.push(href, undefined, {
        shallow: true
      });
    };
    $[56] = hrefForRow;
    $[57] = router;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  const handleSelect = t19;
  const isInitialLoading = boardQuery.isInitialLoading || foundersQuery.loading && !foundersQuery.data;
  let t20;
  let t21;
  if ($[59] !== foundersQuery || $[60] !== unreadCount) {
    t20 = () => {
      if (prevUnreadRef.current === null) {
        prevUnreadRef.current = unreadCount;
        return;
      }
      if (prevUnreadRef.current === unreadCount) {
        return;
      }
      prevUnreadRef.current = unreadCount;
      foundersQuery.refetch();
    };
    t21 = [unreadCount, foundersQuery];
    $[59] = foundersQuery;
    $[60] = unreadCount;
    $[61] = t20;
    $[62] = t21;
  } else {
    t20 = $[61];
    t21 = $[62];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[63] !== canAccessAdvisorTeam || $[64] !== hrefForRow || $[65] !== isInitialLoading || $[66] !== isMobile || $[67] !== router || $[68] !== rows || $[69] !== selectedAdvisorKey || $[70] !== selectedAdvisorUserId || $[71] !== selectedFounderUserId) {
    t22 = () => {
      if (!router.isReady || isInitialLoading || didDefaultSelectRef.current) {
        return;
      }
      if (selectedAdvisorUserId || selectedFounderUserId || selectedAdvisorKey) {
        didDefaultSelectRef.current = true;
        return;
      }
      if (isMobile) {
        didDefaultSelectRef.current = true;
        return;
      }
      const first = pickDefaultInboxRow(rows, canAccessAdvisorTeam);
      didDefaultSelectRef.current = true;
      if (!first) {
        return;
      }
      router.replace(hrefForRow(first), undefined, {
        shallow: true
      });
    };
    t23 = [router, isInitialLoading, rows, selectedAdvisorUserId, selectedFounderUserId, selectedAdvisorKey, hrefForRow, canAccessAdvisorTeam, isMobile];
    $[63] = canAccessAdvisorTeam;
    $[64] = hrefForRow;
    $[65] = isInitialLoading;
    $[66] = isMobile;
    $[67] = router;
    $[68] = rows;
    $[69] = selectedAdvisorKey;
    $[70] = selectedAdvisorUserId;
    $[71] = selectedFounderUserId;
    $[72] = t22;
    $[73] = t23;
  } else {
    t22 = $[72];
    t23 = $[73];
  }
  useEffect(t22, t23);
  let t24;
  if ($[74] !== selectedRow) {
    t24 = selectedRow ? conversationRowKey(selectedRow) : null;
    $[74] = selectedRow;
    $[75] = t24;
  } else {
    t24 = $[75];
  }
  const selectedKey = t24;
  const hasThreadParam = !!(selectedAdvisorUserId || selectedFounderUserId || selectedAdvisorKey);
  let t25;
  if ($[76] !== selectedRow) {
    t25 = selectedRow ? selectedRow.kind === "founder" ? founderDisplayName(selectedRow.engagement) : selectedRow.kind === "locked" ? selectedRow.advisor.name : selectedRow.kind === "direct" ? selectedRow.name : engagementDisplayName(selectedRow.engagement) : "Profile";
    $[76] = selectedRow;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  const profileTitle = t25;
  let t26;
  if ($[78] !== selectedKey || $[79] !== selectedRow) {
    t26 = selectedRow ? selectedRow.kind === "locked" ? <Box flex={1} minHeight={0} overflowY="auto" padding={2} width="full"><LockedTeamAdvisorMiniProfile advisor={selectedRow.advisor} /></Box> : selectedRow.kind === "direct" ? <Box flex={1} minHeight={0} overflowY="auto" padding={2} width="full"><AdvisorProfileCard userId={selectedRow.userId} /></Box> : <Box flex={1} height="full" minHeight={0} padding={2} width="full"><EngagementAsidePanel key={selectedKey || "profile"} engagement={selectedRow.engagement} profilePerspective={selectedRow.kind === "founder" ? "founder" : "advisor"} /></Box> : <Text color="colorPalette.fg/60" fontSize="sm" padding={4} textAlign="center">Select a conversation to see their profile.</Text>;
    $[78] = selectedKey;
    $[79] = selectedRow;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  const profilePanel = t26;
  let t27;
  if ($[81] !== foundersHasNext || $[82] !== loadMoreFounders || $[83] !== loadingMoreFounders) {
    t27 = <Box paddingBottom={2} paddingX={2}><AdvisorsLoadMore hasNextPage={foundersHasNext} loading={loadingMoreFounders} onLoadMore={loadMoreFounders} /></Box>;
    $[81] = foundersHasNext;
    $[82] = loadMoreFounders;
    $[83] = loadingMoreFounders;
    $[84] = t27;
  } else {
    t27 = $[84];
  }
  let t28;
  if ($[85] !== hasRealThread || $[86] !== rows.length) {
    t28 = !hasRealThread && rows.length > 0 ? <Box padding={3}><InboxStartCtas size="xs" /></Box> : null;
    $[85] = hasRealThread;
    $[86] = rows.length;
    $[87] = t28;
  } else {
    t28 = $[87];
  }
  let t29;
  if ($[88] !== t27 || $[89] !== t28) {
    t29 = <>{t27}{t28}</>;
    $[88] = t27;
    $[89] = t28;
    $[90] = t29;
  } else {
    t29 = $[90];
  }
  let t30;
  if ($[91] !== handleSelect || $[92] !== isInitialLoading || $[93] !== rows || $[94] !== selectedKey || $[95] !== t29) {
    t30 = <InboxNavColumn isInitialLoading={isInitialLoading} rows={rows} selectedKey={selectedKey} listFooter={t29} onSelect={handleSelect} />;
    $[91] = handleSelect;
    $[92] = isInitialLoading;
    $[93] = rows;
    $[94] = selectedKey;
    $[95] = t29;
    $[96] = t30;
  } else {
    t30 = $[96];
  }
  const inboxNavColumn = t30;
  const t31 = boardQuery.error || foundersQuery.error;
  let t32;
  if ($[97] !== inboxNavColumn || $[98] !== isMobile) {
    t32 = isMobile ? [] : [{
      id: INBOX_NAV_ID,
      defaultWidth: 260,
      minWidth: 180,
      maxWidth: 320,
      isResizable: true,
      resizeHandlePosition: "right",
      useDrawerBreakpoints: {
        base: true,
        md: false
      },
      containerProps: attachedNavPanelContainerProps,
      children: inboxNavColumn
    }];
    $[97] = inboxNavColumn;
    $[98] = isMobile;
    $[99] = t32;
  } else {
    t32 = $[99];
  }
  let t33;
  if ($[100] !== canAccessAdvisorTeam || $[101] !== currentUser?.userId || $[102] !== hasThreadParam || $[103] !== inboxNavColumn || $[104] !== isInitialLoading || $[105] !== isMobile || $[106] !== paths.inbox || $[107] !== profileTitle || $[108] !== selectedAdvisorUserId || $[109] !== selectedFounderUserId || $[110] !== selectedRow) {
    t33 = {
      id: "advisors-inbox-main",
      containerProps: attachedPanelContainerProps,
      children: isMobile && !selectedRow && !hasThreadParam ? inboxNavColumn : <InboxThreadChrome backHref={isMobile ? paths.inbox : undefined} title={selectedRow ? profileTitle : "Messages"}><InboxChatPane canAccessAdvisorTeam={canAccessAdvisorTeam} currentUserId={currentUser?.userId} loading={isInitialLoading} requestedAdvisorUserId={selectedAdvisorUserId} requestedFounderUserId={selectedFounderUserId} row={selectedRow} /></InboxThreadChrome>
    };
    $[100] = canAccessAdvisorTeam;
    $[101] = currentUser?.userId;
    $[102] = hasThreadParam;
    $[103] = inboxNavColumn;
    $[104] = isInitialLoading;
    $[105] = isMobile;
    $[106] = paths.inbox;
    $[107] = profileTitle;
    $[108] = selectedAdvisorUserId;
    $[109] = selectedFounderUserId;
    $[110] = selectedRow;
    $[111] = t33;
  } else {
    t33 = $[111];
  }
  let t34;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = {
      base: true,
      lg: false
    };
    $[112] = t34;
  } else {
    t34 = $[112];
  }
  let t35;
  if ($[113] !== profilePanel) {
    t35 = {
      id: INBOX_ASIDE_ID,
      defaultWidth: 320,
      minWidth: 260,
      maxWidth: 440,
      isResizable: true,
      isPanelOpen: false,
      resizeHandlePosition: "left",
      useDrawerBreakpoints: t34,
      containerProps: attachedAsidePanelContainerProps,
      children: profilePanel
    };
    $[113] = profilePanel;
    $[114] = t35;
  } else {
    t35 = $[114];
  }
  let t36;
  if ($[115] !== t32 || $[116] !== t33 || $[117] !== t35) {
    t36 = <NestedAppLayout id="advisors-inbox" panels={[...t32, t33, t35]} />;
    $[115] = t32;
    $[116] = t33;
    $[117] = t35;
    $[118] = t36;
  } else {
    t36 = $[118];
  }
  let t37;
  if ($[119] !== t31 || $[120] !== t36) {
    t37 = <AdvisorsQueryState error={t31} loading={false}>{t36}</AdvisorsQueryState>;
    $[119] = t31;
    $[120] = t36;
    $[121] = t37;
  } else {
    t37 = $[121];
  }
  return t37;
};